import { useState, useCallback, useMemo } from "react";
import { validateWinnerInfo } from "../../utils/newyear/validators";

const useFormValidation = (initialValues = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const setValue = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const setFieldTouched = useCallback((field, isTouched = true) => {
    setTouched((prev) => ({ ...prev, [field]: isTouched }));
  }, []);

  const handleChange = useCallback(
    (field) => (e) => {
      const value = e?.target?.value ?? e;
      setValue(field, value);
    },
    [setValue]
  );

  const handleBlur = useCallback(
    (field) => () => {
      setFieldTouched(field, true);
    },
    [setFieldTouched]
  );

  const validate = useCallback(() => {
    const { isValid, errors: validationErrors } = validateWinnerInfo(
      values.name,
      values.phone
    );
    setErrors(validationErrors);
    return isValid;
  }, [values]);

  const validateField = useCallback(
    (field) => {
      const { errors: validationErrors } = validateWinnerInfo(
        values.name,
        values.phone
      );
      if (validationErrors[field]) {
        setErrors((prev) => ({ ...prev, [field]: validationErrors[field] }));
        return false;
      }
      setErrors((prev) => ({ ...prev, [field]: null }));
      return true;
    },
    [values]
  );

  const reset = useCallback(
    (newValues = initialValues) => {
      setValues(newValues);
      setErrors({});
      setTouched({});
    },
    [initialValues]
  );

  const getFieldError = useCallback(
    (field) => {
      return touched[field] ? errors[field] : null;
    },
    [touched, errors]
  );

  const isValid = useMemo(() => {
    const { isValid: valid } = validateWinnerInfo(values.name, values.phone);
    return valid;
  }, [values]);

  const isDirty = useMemo(() => {
    return Object.keys(touched).some((key) => touched[key]);
  }, [touched]);

  return {
    values,
    errors,
    touched,
    isValid,
    isDirty,
    setValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    validate,
    validateField,
    reset,
    getFieldError,
  };
};

export default useFormValidation;
